import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  Plus,
  Edit,
  Trash2,
  Eye,
  Calendar,
  FileText,
  ArrowRight
} from 'lucide-react';
import { 
  Button, 
  Modal, 
  Form, 
  Input, 
  Card, 
  message,
  Space,
  Tooltip,
  Row,
  Col,
  Statistic
} from 'antd';
import DashboardLayout from '../components/layout/DashboardLayout';
import CustomTable from '../components/common/CustomTable';
import api from '../config/api';
import dayjs from 'dayjs';

const { TextArea } = Input;

const Sessions = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  
  // Modal states
  const [createModal, setCreateModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  
  // Form instances
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const response = await api.get('/sessions');
      if (response.data.success) {
        setSessions(response.data.sessions);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
      message.error('Failed to fetch sessions');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = async (values) => {
    try {
      setLoading(true);
      const response = await api.post('/sessions', values);
      if (response.data.success) {
        message.success('Session created successfully!');
        fetchSessions();
        setCreateModal(false);
        createForm.resetFields();
      }
    } catch (error) {
      console.error('Error creating session:', error);
      message.error(error.response?.data?.message || 'Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  const handleEditSession = async (values) => {
    if (!selectedSession) return;

    try {
      setLoading(true);
      const response = await api.put(`/sessions/${selectedSession._id}`, {
        sessionName: values.sessionName,
        description: values.description
      });
      
      if (response.data.success) {
        message.success('Session updated successfully!');
        fetchSessions();
        setEditModal(false);
        setSelectedSession(null);
        editForm.resetFields();
      }
    } catch (error) {
      console.error('Error updating session:', error);
      message.error(error.response?.data?.message || 'Failed to update session');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = (session) => {
    Modal.confirm({
      title: 'Delete Session',
      content: (
        <div>
          <p>Are you sure you want to delete the session "<strong>{session.sessionName}</strong>"?</p>
          <p className="text-red-600 mt-2">
            ⚠️ This will permanently delete all {session.totalTransactions || 0} transactions in this session.
          </p>
          <p className="text-gray-600 text-sm mt-2">This action cannot be undone.</p>
        </div>
      ),
      okText: 'Delete Session',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          setLoading(true);
          const response = await api.delete(`/sessions/${session._id}`);
          if (response.data.success) {
            message.success('Session deleted successfully!');
            fetchSessions();
          }
        } catch (error) {
          console.error('Error deleting session:', error);
          message.error(error.response?.data?.message || 'Failed to delete session');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleSessionClick = async (session) => {
    try {
      setLoading(true);
      const response = await api.put(`/sessions/${session._id}/activate`);
      if (response.data.success) {
        message.success(`Switched to ${session.sessionName}`);
        navigate('/accounts');
      }
    } catch (error) {
      console.error('Error switching session:', error);
      message.error('Failed to switch session');
    } finally {
      setLoading(false);
    }
  };

  // Responsive columns for sessions table
  const columns = [
    {
      title: 'Session Name',
      dataIndex: 'sessionName',
      key: 'sessionName',
      width: 200,
      render: (text, record) => (
        <div 
          className="cursor-pointer hover:text-blue-600 font-medium"
          onClick={() => handleSessionClick(record)}
        >
          <div className="flex items-center text-xs">
            <span className="truncate">{text}</span>
            <ArrowRight className="w-3 h-3 ml-2 opacity-50 flex-shrink-0" />
          </div>
        </div>
      )
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 150,
      ellipsis: true,
      responsive: ['md'],
      render: (text) => (
        <Tooltip title={text}>
          <span className="text-xs">{text || 'No description'}</span>
        </Tooltip>
      )
    },
    {
      title: 'Trans.',
      dataIndex: 'totalTransactions',
      key: 'totalTransactions',
      width: 70,
      align: 'center',
      render: (count) => (
        <span className="font-medium text-xs">{count || 0}</span>
      )
    },
    {
      title: 'Balance',
      dataIndex: 'currentBalance',
      key: 'currentBalance',
      width: 90,
      align: 'right',
      render: (balance) => (
        <span className={`font-bold text-xs ${balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          ₹{Math.abs(balance || 0).toLocaleString()}
        </span>
      )
    },
    {
      title: 'Modified',
      dataIndex: 'lastModified',
      key: 'lastModified',
      width: 100,
      responsive: ['lg'],
      render: (date) => (
        <span className="text-xs">
          {dayjs(date).format('DD/MM/YY HH:mm')}
        </span>
      )
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_, record) => (
        <Space size={2}>
          <Tooltip title="View Details">
            <Button 
              type="text" 
              size="small"
              icon={<Eye size={12} />}
              className="!p-1 !min-w-0"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedSession(record);
                setViewModal(true);
              }}
            />
          </Tooltip>
          
          <Tooltip title="Edit Session">
            <Button 
              type="text" 
              size="small"
              icon={<Edit size={12} />}
              className="!p-1 !min-w-0"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedSession(record);
                editForm.setFieldsValue({
                  sessionName: record.sessionName,
                  description: record.description
                });
                setEditModal(true);
              }}
            />
          </Tooltip>
          
          <Tooltip title="Delete Session">
            <Button 
              type="text" 
              size="small"
              danger
              icon={<Trash2 size={12} />}
              className="!p-1 !min-w-0"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSession(record);
              }}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

const headerActions = [
  <Button 
    key="create"
    type="primary" 
    icon={<Plus size={14} />}
    size="small"
    onClick={() => setCreateModal(true)}
  >
    <span className="hidden sm:inline text-xs">Create Session</span>
  </Button>
];

  const breadcrumbs = [
    { title: 'Dashboard', onClick: () => navigate('/dashboard') },
    { title: 'Sessions' }
  ];

  return (
    <DashboardLayout  
      title="Account Sessions" 
      subtitle="Manage and organize your account sessions"
      headerActions={headerActions}
      breadcrumbs={breadcrumbs}
    >
      <div className="space-y-4 m-4">
        {/* Stats */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title={<span className="text-xs">Total Sessions</span>}
                value={sessions.length}
                prefix={<FileText className="w-3 h-3" />}
                valueStyle={{ fontSize: '14px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title={<span className="text-xs">Total Transactions</span>}
                value={sessions.reduce((sum, s) => sum + (s.totalTransactions || 0), 0)}
                prefix={<Calendar className="w-3 h-3" />}
                valueStyle={{ fontSize: '14px' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={8}>
            <Card size="small">
              <Statistic
                title={<span className="text-xs">Total Balance</span>}
                value={Math.abs(sessions.reduce((sum, s) => sum + (s.currentBalance || 0), 0)).toLocaleString()}
                prefix="₹"
                valueStyle={{ fontSize: '14px' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Instructions */}
        <Card className="bg-blue-50 border-blue-200" size="small">
          <div className="flex items-start">
            <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center mr-3 flex-shrink-0 mt-0.5">
              <FileText className="w-3 h-3 text-white" />
            </div>
            <div>
              <p className="text-blue-800 font-medium text-xs">
                Click on any session name to switch to that session and manage its transactions.
              </p>
              <p className="text-blue-600 text-xs">
                Use the action buttons to view, edit, or delete sessions.
              </p>
            </div>
          </div>
        </Card>

        {/* Sessions Table */}
        <Card size="small" className="overflow-hidden">
          <CustomTable
            columns={columns}
            dataSource={sessions}
            loading={loading}
            scroll={{ x: 'max-content', y: 350 }}
            pagination={{
              pageSize: 10,
              showTotal: (total, range) => (
                <span className="text-xs">
                  {window.innerWidth >= 640 
                    ? `${range[0]}-${range[1]} of ${total} sessions`
                    : `${total} total`
                  }
                </span>
              )
            }}
            onRow={(record) => ({
              style: { cursor: 'pointer' },
              onMouseEnter: (e) => {
                e.target.closest('tr').style.backgroundColor = '#f0f9ff';
              },
              onMouseLeave: (e) => {
                e.target.closest('tr').style.backgroundColor = '';
              }
            })}
          />
        </Card>
      </div>

      {/* Create Session Modal */}
      <Modal
        title={<span className="text-sm font-semibold">Create New Session</span>}
        open={createModal}
        onCancel={() => {
          setCreateModal(false);
          createForm.resetFields();
        }}
        footer={null}
        width="min(90vw, 500px)"
        centered
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateSession}
          className="mt-4"
        >
          <Form.Item
            name="sessionName"
            label={<span className="text-xs font-medium">Session Name</span>}
            rules={[{ required: true, message: 'Please enter session name' }]}
          >
            <Input placeholder="e.g., March 2025 Accounts" className="text-xs" />
          </Form.Item>

          <Form.Item 
            name="description" 
            label={<span className="text-xs font-medium">Description</span>}
          >
            <TextArea 
              rows={3} 
              placeholder="Enter session description (optional)"
              maxLength={500}
              showCount
              className="text-xs"
            />
          </Form.Item>

          <div className="flex justify-end gap-2">
            <Button 
              onClick={() => {
                setCreateModal(false);
                createForm.resetFields();
              }}
              size="small"
            >
              <span className="text-xs">Cancel</span>
            </Button>
            <Button type="primary" htmlType="submit" loading={loading} size="small">
              <span className="text-xs">Create Session</span>
            </Button>
          </div>
        </Form>
      </Modal>

      {/* Edit Session Modal */}
      <Modal
        title={<span className="text-sm font-semibold">Edit Session</span>}
        open={editModal}
        onCancel={() => {
          setEditModal(false);
          setSelectedSession(null);
          editForm.resetFields();
        }}
        footer={null}
        width="min(90vw, 500px)"
        centered
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSession}
          className="mt-4"
        >
          <Form.Item
            name="sessionName"
            label={<span className="text-xs font-medium">Session Name</span>}
            rules={[{ required: true, message: 'Please enter session name' }]}
          >
            <Input placeholder="Enter session name" className="text-xs" />
          </Form.Item>

          <Form.Item 
            name="description" 
            label={<span className="text-xs font-medium">Description</span>}
          >
            <TextArea 
              rows={3} 
              placeholder="Enter session description (optional)"
              maxLength={500}
              showCount
              className="text-xs"
            />
          </Form.Item>

          <div className="flex justify-end gap-2">
            <Button 
              onClick={() => {
                setEditModal(false);
                setSelectedSession(null);
                editForm.resetFields();
              }}
              size="small"
            >
              <span className="text-xs">Cancel</span>
            </Button>
            <Button type="primary" htmlType="submit" loading={loading} size="small">
              <span className="text-xs">Update Session</span>
            </Button>
          </div>
        </Form>
      </Modal>

      {/* View Session Modal */}
      <Modal
        title={<span className="text-sm font-semibold">Session Details</span>}
        open={viewModal}
        onCancel={() => setViewModal(false)}
        footer={[
          <Button key="close" onClick={() => setViewModal(false)} size="small">
            <span className="text-xs">Close</span>
          </Button>,
          <Button 
            key="switch" 
            type="primary" 
            size="small"
            onClick={() => {
              if (selectedSession) {
                handleSessionClick(selectedSession);
                setViewModal(false);
              }
            }}
          >
            <span className="text-xs">Switch to This Session</span>
          </Button>
        ]}
        width="min(90vw, 600px)"
        centered
      >
        {selectedSession && (
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm">{selectedSession.sessionName}</h3>
            </div>
            
            <div>
              <strong className="text-xs">Description:</strong>
              <p className="mt-1 text-xs">{selectedSession.description || 'No description provided'}</p>
            </div>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title={<span className="text-xs">Total Transactions</span>}
                    value={selectedSession.totalTransactions || 0}
                    valueStyle={{ fontSize: '14px' }}
                  />
                </Card>
              </Col>
              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title={<span className="text-xs">Current Balance</span>}
                    value={Math.abs(selectedSession.currentBalance || 0).toLocaleString()}
                    prefix="₹"
                    valueStyle={{ 
                      color: selectedSession.currentBalance >= 0 ? '#52c41a' : '#ff4d4f',
                      fontSize: '14px'
                    }}
                  />
                </Card>
              </Col>
            </Row>

            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <strong>Created:</strong>
                <p>{dayjs(selectedSession.createdAt).format('DD/MM/YYYY HH:mm')}</p>
              </div>
              <div>
                <strong>Last Modified:</strong>
                <p>{dayjs(selectedSession.lastModified).format('DD/MM/YYYY HH:mm')}</p>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </DashboardLayout>
  );
};

export default Sessions;